// Gemeinsame Typen und Zeichenregeln des Whiteboards (Server: Vorschau/Prüfung, Web: Brett/Export).
export const WB_ARTEN = ['stift', 'linie', 'pfeil', 'rechteck', 'ellipse', 'raute', 'text', 'notiz', 'bild'];
/** Hintergrund einer Tafel: wie das Design der App (hell/dunkel nach System), immer hell oder immer dunkel. */
export const WB_TAFELFARBEN = ['design', 'hell', 'dunkel'];
export const WB_TAFELFARBE_NAMEN = { design: 'Wie das Design', hell: 'Hell', dunkel: 'Dunkel' };
/** Dunkle Tafel: Grund und getauschte Tinte (wie im Brett, damit Export und Vorschau gleich aussehen). */
export const WB_DUNKEL = { grund: '#181b22', tinte: '#e8eaef', papier: '#101217' };
export const WB_FARBEN = ['#1e1e1e', '#e03131', '#2f9e44', '#1971c2', '#f08c00', '#9c36b5', '#868e96', '#ffffff'];
export const WB_FUELLUNGEN = ['#ffc9c9', '#b2f2bb', '#a5d8ff', '#ffec99', '#eebefa', '#e9ecef'];
export const WB_NOTIZ_FARBEN = ['#fff3a3', '#ffd8a8', '#c3fae8', '#d0ebff', '#fcc2d7'];
export const WB_MAX_ELEMENTE = 20_000;
export const WB_MAX_PUNKTE = 20_000;
export const WB_NAMEN = {
    stift: ['Strich', 'Striche'],
    linie: ['Linie', 'Linien'],
    pfeil: ['Pfeil', 'Pfeile'],
    rechteck: ['Rechteck', 'Rechtecke'],
    ellipse: ['Ellipse', 'Ellipsen'],
    raute: ['Raute', 'Rauten'],
    text: ['Text', 'Texte'],
    notiz: ['Haftnotiz', 'Haftnotizen'],
    bild: ['Bild', 'Bilder'],
};
/** Vorgaben für neue Elemente. */
export function wbStandard(art, id) {
    return {
        id,
        art,
        x: 0,
        y: 0,
        b: art === 'notiz' ? 200 : 0,
        h: art === 'notiz' ? 160 : 0,
        drehung: 0,
        farbe: '#1e1e1e',
        fuellung: art === 'notiz' ? WB_NOTIZ_FARBEN[0] : null,
        staerke: 3,
        punkte: null,
        text: '',
        schrift: art === 'notiz' ? 18 : art === 'text' ? 24 : 20,
        dateiId: null,
        gruppe: null,
        z: 0,
        start: null,
        ende: null,
        geloescht: false,
        version: 0,
    };
}
/** Achsparalleler Kasten um ein (evtl. gedrehtes) Element. */
export function wbHuelle(e) {
    if (!e.drehung)
        return { x: e.x, y: e.y, b: e.b, h: e.h };
    const cx = e.x + e.b / 2;
    const cy = e.y + e.h / 2;
    const w = (e.drehung * Math.PI) / 180;
    const c = Math.abs(Math.cos(w));
    const s = Math.abs(Math.sin(w));
    const b = e.b * c + e.h * s;
    const h = e.b * s + e.h * c;
    return { x: cx - b / 2, y: cy - h / 2, b, h };
}
export function wbHuelleAlle(liste) {
    if (!liste.length)
        return null;
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
    for (const e of liste) {
        const k = wbHuelle(e);
        x1 = Math.min(x1, k.x);
        y1 = Math.min(y1, k.y);
        x2 = Math.max(x2, k.x + k.b);
        y2 = Math.max(y2, k.y + k.h);
    }
    return { x: x1, y: y1, b: x2 - x1, h: y2 - y1 };
}
/** Grobe Breite eines Textes (gleich in Brett, Export und Vorschau). */
export function wbTextBreite(text, schrift) {
    let breite = 0;
    for (const z of text)
        breite += /[ilI.,:;'|!]/.test(z) ? 0.3 : /[mwMW@]/.test(z) ? 0.85 : /[A-ZÄÖÜ]/.test(z) ? 0.66 : 0.55;
    return breite * schrift;
}
/** Text in Zeilen umbrechen; ohne `breite` nur an Zeilenumbrüchen. */
export function wbZeilen(text, schrift, breite) {
    const aus = [];
    for (const absatz of text.split('\n')) {
        if (!breite) {
            aus.push(absatz);
            continue;
        }
        let zeile = '';
        for (const wort of absatz.split(/(\s+)/)) {
            if (!wort)
                continue;
            const probe = zeile + wort;
            if (wbTextBreite(probe, schrift) <= breite || !zeile.trim()) {
                // Sehr lange Wörter hart umbrechen
                if (wbTextBreite(probe, schrift) > breite && !zeile.trim()) {
                    let rest = probe;
                    while (wbTextBreite(rest, schrift) > breite && rest.length > 1) {
                        let n = rest.length - 1;
                        while (n > 1 && wbTextBreite(rest.slice(0, n), schrift) > breite)
                            n--;
                        aus.push(rest.slice(0, n));
                        rest = rest.slice(n);
                    }
                    zeile = rest;
                }
                else
                    zeile = probe;
            }
            else {
                aus.push(zeile.trimEnd());
                zeile = wort.trimStart();
            }
        }
        aus.push(zeile.trimEnd());
    }
    return aus;
}
/** Kastengröße eines freien Textes. */
export function wbTextKasten(text, schrift) {
    const zeilen = wbZeilen(text || ' ', schrift);
    const b = Math.max(schrift, ...zeilen.map((z) => wbTextBreite(z, schrift))) + 4;
    return { b, h: zeilen.length * schrift * 1.25 + 4 };
}
// ---------------------------------------------------------------- SVG
const n = (v) => String(Math.round(v * 10) / 10);
export function wbEscape(t) {
    return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
const FARBE = /^#[0-9a-fA-F]{6}$/;
const farbe = (f, ersatz = 'none') => (f && FARBE.test(f) ? f : ersatz);
/** Glatter Pfad durch Punkte (quadratische Kurven über die Mittelpunkte). */
export function wbGlatterPfad(p) {
    if (p.length < 2)
        return '';
    if (p.length < 4)
        return `M${n(p[0])} ${n(p[1])}l0.1 0`;
    if (p.length === 4)
        return `M${n(p[0])} ${n(p[1])}L${n(p[2])} ${n(p[3])}`;
    let d = `M${n(p[0])} ${n(p[1])}`;
    for (let i = 2; i < p.length - 2; i += 2) {
        const mx = (p[i] + p[i + 2]) / 2;
        const my = (p[i + 1] + p[i + 3]) / 2;
        d += `Q${n(p[i])} ${n(p[i + 1])} ${n(mx)} ${n(my)}`;
    }
    d += `L${n(p[p.length - 2])} ${n(p[p.length - 1])}`;
    return d;
}
/** Inneres SVG eines Elements (eine <g> mit Lage und Drehung). Alle Texte sind maskiert. */
export function wbElementSvg(e, o = {}) {
    const b = Math.max(e.b, 0);
    const h = Math.max(e.h, 0);
    const lage = `translate(${n(e.x)} ${n(e.y)})${e.drehung ? ` rotate(${n(e.drehung)} ${n(b / 2)} ${n(h / 2)})` : ''}`;
    const strich = farbe(e.farbe, '#1e1e1e');
    const fuell = farbe(e.fuellung);
    const st = n(Math.max(0.5, e.staerke));
    const pe = o.treffer ? ' pointer-events="all"' : '';
    let innen = '';
    switch (e.art) {
        case 'stift': {
            const d = wbGlatterPfad(e.punkte ?? []);
            if (o.treffer)
                innen += `<path d="${d}" fill="none" stroke="transparent" stroke-width="${n(Math.max(14, e.staerke + 10))}" stroke-linecap="round" stroke-linejoin="round" pointer-events="stroke"/>`;
            innen += `<path d="${d}" fill="none" stroke="${strich}" stroke-width="${st}" stroke-linecap="round" stroke-linejoin="round"/>`;
            break;
        }
        case 'linie':
        case 'pfeil': {
            const p = e.punkte ?? [0, 0, b, h];
            const [x1, y1, x2, y2] = [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0, p[3] ?? 0];
            if (o.treffer)
                innen += `<line x1="${n(x1)}" y1="${n(y1)}" x2="${n(x2)}" y2="${n(y2)}" stroke="transparent" stroke-width="${n(Math.max(14, e.staerke + 10))}" pointer-events="stroke"/>`;
            innen += `<line x1="${n(x1)}" y1="${n(y1)}" x2="${n(x2)}" y2="${n(y2)}" stroke="${strich}" stroke-width="${st}" stroke-linecap="round"/>`;
            if (e.art === 'pfeil') {
                const w = Math.atan2(y2 - y1, x2 - x1);
                const l = Math.min(Math.hypot(x2 - x1, y2 - y1) * 0.5, 10 + e.staerke * 3);
                const a = 0.45;
                const ax = x2 - l * Math.cos(w - a), ay = y2 - l * Math.sin(w - a);
                const bx = x2 - l * Math.cos(w + a), by = y2 - l * Math.sin(w + a);
                innen += `<path d="M${n(ax)} ${n(ay)}L${n(x2)} ${n(y2)}L${n(bx)} ${n(by)}" fill="none" stroke="${strich}" stroke-width="${st}" stroke-linecap="round" stroke-linejoin="round"/>`;
            }
            break;
        }
        case 'rechteck':
            innen = `<rect x="0" y="0" width="${n(b)}" height="${n(h)}" rx="${n(Math.min(8, b / 4, h / 4))}" fill="${fuell}" stroke="${strich}" stroke-width="${st}"${pe}/>`;
            innen += beschriftung(e, b, h, strich);
            break;
        case 'ellipse':
            innen = `<ellipse cx="${n(b / 2)}" cy="${n(h / 2)}" rx="${n(b / 2)}" ry="${n(h / 2)}" fill="${fuell}" stroke="${strich}" stroke-width="${st}"${pe}/>`;
            innen += beschriftung(e, b, h, strich);
            break;
        case 'raute':
            innen = `<path d="M${n(b / 2)} 0L${n(b)} ${n(h / 2)}L${n(b / 2)} ${n(h)}L0 ${n(h / 2)}Z" fill="${fuell}" stroke="${strich}" stroke-width="${st}" stroke-linejoin="round"${pe}/>`;
            innen += beschriftung(e, b, h, strich);
            break;
        case 'text': {
            if (o.treffer)
                innen += `<rect width="${n(b)}" height="${n(h)}" fill="transparent" pointer-events="all"/>`;
            innen += textSvg(wbZeilen(e.text, e.schrift), e.schrift, 2, 2, strich);
            break;
        }
        case 'notiz': {
            const rand = 12;
            innen += `<rect x="0" y="0" width="${n(b)}" height="${n(h)}" rx="4" fill="${farbe(e.fuellung, WB_NOTIZ_FARBEN[0])}" stroke="#00000022" stroke-width="1"${pe}/>`;
            innen += textSvg(wbZeilen(e.text, e.schrift, Math.max(10, b - 2 * rand)), e.schrift, rand, rand, strich);
            break;
        }
        case 'bild': {
            const adr = e.dateiId && o.bildAdresse ? o.bildAdresse(e.dateiId) : null;
            if (adr) {
                if (o.treffer)
                    innen += `<rect width="${n(b)}" height="${n(h)}" fill="transparent" pointer-events="all"/>`;
                innen += `<image href="${wbEscape(adr)}" x="0" y="0" width="${n(b)}" height="${n(h)}" preserveAspectRatio="none"/>`;
            }
            else {
                innen = `<rect width="${n(b)}" height="${n(h)}" fill="#e9ecef" stroke="#adb5bd" stroke-width="1"${pe}/><path d="M${n(b * 0.2)} ${n(h * 0.75)}L${n(b * 0.42)} ${n(h * 0.45)}L${n(b * 0.58)} ${n(h * 0.62)}L${n(b * 0.68)} ${n(h * 0.52)}L${n(b * 0.8)} ${n(h * 0.75)}Z" fill="#ced4da"/>`;
            }
            break;
        }
    }
    return `<g transform="${lage}">${innen}</g>`;
}
function textSvg(zeilen, schrift, x, y, farbe, mittig = false) {
    const zh = schrift * 1.25;
    const tspans = zeilen
        .map((z, i) => `<tspan x="${n(x)}" y="${n(y + schrift * 0.95 + i * zh)}">${wbEscape(z) || ' '}</tspan>`)
        .join('');
    return `<text font-family="system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif" font-size="${n(schrift)}" fill="${farbe}"${mittig ? ' text-anchor="middle"' : ''} xml:space="preserve">${tspans}</text>`;
}
/** Nutzbare Textbreite in einer Form (Ellipse und Raute sind innen schmaler). */
export function wbBeschriftungBreite(art, b) {
    const f = art === 'ellipse' ? 0.72 : art === 'raute' ? 0.52 : 1;
    return Math.max(10, b * f - 16);
}
/** Beschriftung mittig in Rechteck, Ellipse oder Raute. */
function beschriftung(e, b, h, farbe) {
    if (!e.text.trim())
        return '';
    const zeilen = wbZeilen(e.text, e.schrift, wbBeschriftungBreite(e.art, b));
    const hoehe = zeilen.length * e.schrift * 1.25;
    return textSvg(zeilen, e.schrift, b / 2, h / 2 - hoehe / 2, farbe, true);
}
/** Sichtbare Elemente in Zeichenreihenfolge. */
export function wbSortiert(liste) {
    return [...liste].filter((e) => !e.geloescht).sort((a, b) => a.z - b.z || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
/** Ganze Tafel als eigenständiges SVG (Export und Vorschau). */
export function wbAlsSvg(liste, o = {}) {
    const els = wbSortiert(liste).map((e) => (o.dunkel ? wbDunkleTinte(e) : e));
    const rand = o.rand ?? 20;
    const k = wbHuelleAlle(els) ?? { x: 0, y: 0, b: 400, h: 300 };
    const vb = { x: k.x - rand, y: k.y - rand, b: Math.max(1, k.b + 2 * rand), h: Math.max(1, k.h + 2 * rand) };
    const faktor = o.maxBreite ? Math.min(1, o.maxBreite / vb.b) : 1;
    const breite = vb.b * faktor;
    const hoehe = vb.h * faktor;
    const grund = o.hintergrund ?? (o.dunkel ? WB_DUNKEL.grund : '#ffffff');
    const hg = o.hintergrund === null ? '' : `<rect x="${n(vb.x)}" y="${n(vb.y)}" width="${n(vb.b)}" height="${n(vb.h)}" fill="${farbe(grund, '#ffffff')}"/>`;
    return (`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${n(vb.x)} ${n(vb.y)} ${n(vb.b)} ${n(vb.h)}" width="${n(breite)}" height="${n(hoehe)}">` +
        hg +
        els.map((e) => wbElementSvg(e, { bildAdresse: o.bildAdresse })).join('') +
        `</svg>`);
}
/** Für dunkle Tafeln: schwarze und weiße Tinte tauschen (Haftnotizen behalten ihre dunkle Schrift). */
export function wbDunkleTinte(e) {
    const tausch = (f) => (f === '#1e1e1e' ? WB_DUNKEL.tinte : f === '#ffffff' ? WB_DUNKEL.papier : f);
    if (e.art === 'notiz' || e.art === 'bild')
        return e;
    return { ...e, farbe: tausch(e.farbe) ?? e.farbe, fuellung: tausch(e.fuellung) };
}
/** Farbe für einen Benutzer (Zeiger, Anwesenheit). */
export function wbBenutzerFarbe(id) {
    const farben = ['#e03131', '#2f9e44', '#1971c2', '#f08c00', '#9c36b5', '#0c8599', '#d6336c', '#5c940d'];
    let h = 0;
    for (const z of id)
        h = (h * 31 + z.charCodeAt(0)) >>> 0;
    return farben[h % farben.length];
}
//# sourceMappingURL=whiteboard.js.map